import {
  FETCH_USER_AUTOFILL_SETTINGS,
  UPDATE_USER_AUTOFILL_SETTINGS,
  FETCH_USER_SETTINGS,
  FETCH_USER_SETTINGS_FAILURE,
  UPDATE_USER_TIMEZONE_SETTINGS,
  UPDATE_USER_ADDRESS_SEARCH_RADIUS_SETTINGS,
  UPDATE_USER_MAP_CENTER_SETTINGS,
  UPDATE_USER_ALGORITHM_CONFIDENCE_SETTINGS,
  FETCH_ORGANIZATIONS,
  DECREMENT_ORG_UNREAD_MAINTENANCE_COUNT,
  UPDATE_SUPER_USER_ORGANIZATION,
  FETCH_USERS_BY_ORG,
  FETCH_ALL_USERS,
  UPDATE_USER_PERMISSIONS,
  DELETE_USER,
  MOVE_DEVICE_ORG,
  RENAME_DEVICE,
  CREATE_ORGANIZATION,
  UPDATE_ORGANIZATION,
  CHANGE_DEVICE_HARDWARE_ID,
  UPDATE_USER_SELECTED_DEVICE,
  FETCH_USER_ORGANIZATIONS,
} from "../actions/types";
import _ from "lodash";

export const DEFAULT_USER_SETTINGS = {
  autofillSettings: {
    species: false,
    speciesDisplay: true,
    sex: false,
    sexDisplay: true,
    molecular: false,
    molecularDisplay: true,
    gonotrophic: false,
    gonotrophicDisplay: false,
    lifeStage: false,
    lifeStageDisplay: false,
    skipEmpty: false,
  },
  email: "",
  name: "",
  nickname: "",
  mapCenter: "39.294201, -76.588686",
  addressSearchRadius: 100,
  organization: undefined,
  timeZone: "AUTO",
  superUser: false,
  fetched: false,
  algConfidence: 0,
  unreadCountsVersions: {},
};

/* eslint import/no-anonymous-default-export: [2, {"allowArrowFunction": true}] */
export default (state = DEFAULT_USER_SETTINGS, action) => {
  switch (action.type) {
    case FETCH_USER_SETTINGS:
      return { ..._.defaultsDeep(action.payload, state), fetched: true };
    // `fetched` means the settings request has settled, not that it succeeded -
    // it is what PrivateRoute waits on before deciding between the app and the
    // forbidden page, and without it a refusal is indistinguishable from a
    // request still in flight. No organization is set, which is what sends
    // PrivateRoute down the forbidden branch.
    case FETCH_USER_SETTINGS_FAILURE:
      return { ...state, fetched: true, settingsError: action.payload };
    case UPDATE_USER_TIMEZONE_SETTINGS:
      return _.defaultsDeep(action.payload, state);
    case UPDATE_USER_ADDRESS_SEARCH_RADIUS_SETTINGS:
      return _.defaultsDeep(action.payload, state);
    case UPDATE_USER_ALGORITHM_CONFIDENCE_SETTINGS:
      return _.defaultsDeep(action.payload, state);
    case UPDATE_USER_MAP_CENTER_SETTINGS:
      return _.defaultsDeep(action.payload, state);
    case FETCH_USER_AUTOFILL_SETTINGS:
    case UPDATE_USER_AUTOFILL_SETTINGS:
      return _.defaultsDeep({ autofillSettings: action.payload }, state);
    // The organizations this user may act as, already filtered and named by the
    // backend. Distinct from `organizations` below, which is the superuser admin
    // list of every org in the system.
    case FETCH_USER_ORGANIZATIONS:
      return { ...state, userOrganizations: action.payload || [] };
    case FETCH_ORGANIZATIONS: {
      const prevOrganizations = state.organizations || {};
      const prevVersions = state.unreadCountsVersions || {};
      const sentVersions = action.payload?.countsVersions;
      return {
        ...state,
        organizations: (action.payload?.organizations || []).reduce(
          (curr, org) => {
            const { tools, unreadMaintenanceCount, deviceCount, ...rest } = org;
            // a poll that started before this org's decrement must not undo it
            const staleCount =
              sentVersions !== undefined &&
              (sentVersions[rest.uuid] || 0) !== (prevVersions[rest.uuid] || 0);
            return {
              ...curr,
              [rest.uuid]: {
                ...rest,
                // a fetch that didn't request counts must not wipe the last known one
                unreadMaintenanceCount:
                  !staleCount && unreadMaintenanceCount !== undefined
                    ? unreadMaintenanceCount
                    : prevOrganizations[rest.uuid]?.unreadMaintenanceCount ?? 0,
                // same reason: the superuser panel fetches without counts, and
                // must not wipe the env-narrowed tally back to undefined
                deviceCount:
                  deviceCount !== undefined
                    ? deviceCount
                    : prevOrganizations[rest.uuid]?.deviceCount,
              },
            };
          },
          {}
        ),
        orgDevices: (action.payload?.organizations || []).reduce(
          (curr, org) => ({
            ...curr,
            [org.uuid]: org?.tools || [],
          }),
          {}
        ),
      };
    }
    case DECREMENT_ORG_UNREAD_MAINTENANCE_COUNT: {
      const { organizationId, amount } = action.payload;
      const org = state.organizations?.[organizationId];
      if (!org) {
        return state;
      }
      return {
        ...state,
        organizations: {
          ...state.organizations,
          [organizationId]: {
            ...org,
            unreadMaintenanceCount: Math.max(
              (org.unreadMaintenanceCount || 0) - amount,
              0
            ),
          },
        },
        unreadCountsVersions: {
          ...(state.unreadCountsVersions || {}),
          [organizationId]:
            (state.unreadCountsVersions?.[organizationId] || 0) + 1,
        },
      };
    }
    case UPDATE_SUPER_USER_ORGANIZATION:
      return { ...state, organization: action.payload?.organizationUuid, fetched: false };
    case FETCH_USERS_BY_ORG:
      return _.defaultsDeep(action.payload, state);
    // The whole directory, bucketed by the panel rather than by the server -
    // "unaffiliated" depends on which organizations IDX has, which the user
    // service cannot see. Replaces the old APPROVE_USER case, which moved a
    // single user between two buckets by hand and could not represent a user
    // belonging to more than one organization.
    case FETCH_ALL_USERS:
      return {
        ...state,
        allUsers: action.payload.users || [],
        allUsersTruncated: !!action.payload.truncated,
      };
    // Replace the one user in place, from the write's own response. Both the
    // memberships and the primary organization come back from Auth0, so the
    // unaffiliated bucket and the permissions dialog are correct on the next
    // render rather than after a page refresh.
    case UPDATE_USER_PERMISSIONS: {
      const updated = action.payload?.user;
      if (!updated?.user_id) return state;
      return {
        ...state,
        allUsers: (state.allUsers || []).map((u) =>
          u.user_id === updated.user_id ? { ...u, ...updated } : u
        ),
      };
    }
    case DELETE_USER:
      return {
        ...state,
        orgUsers: {
          ...state.orgUsers,
          [action.payload.organization]: Object.fromEntries(
            Object.entries(
              state?.orgUsers?.[action.payload.organization] || {}
            ).filter(([k, v]) => k !== action.payload.userId)
          ),
        },
      };
    case MOVE_DEVICE_ORG:
      let movedDevice;
      const oldOrgState = state.orgDevices?.[action.payload.oldOrg].reduce(
        (arr, device) => {
          if (device.uuid !== action.payload.device) {
            return [...arr, device];
          }
          movedDevice = device;
          return arr;
        },
        []
      );
      if (movedDevice) {
        return {
          ...state,
          orgDevices: {
            ...state.orgDevices,
            [action.payload.oldOrg]: oldOrgState,
            [action.payload.newOrg]: [
              ...state.orgDevices?.[action.payload.newOrg],
              movedDevice,
            ],
          },
        };
      }
      return state;
    case RENAME_DEVICE:
      return {
        ...state,
        orgDevices: {
          ...state.orgDevices,
          [action.payload.org]: state.orgDevices?.[action.payload.org].map(
            (device) =>
              device.uuid === action.payload.device
                ? {
                  ...device,
                  display_name: action.payload.name,
                }
                : device
          ),
        },
      };
    case CHANGE_DEVICE_HARDWARE_ID:
      return {
        ...state,
        orgDevices: {
          ...state.orgDevices,
          [action.payload.org]: state.orgDevices?.[action.payload.org].map(
            (device) =>
              device.uuid === action.payload.device
                ? {
                  ...device,
                  hardware_id: action.payload.hardwareId,
                }
                : device
          ),
        },
      };
    case CREATE_ORGANIZATION:
      return {
        ...state,
        organizations: {
          ...state.organizations,
          [action.payload.uuid]: action.payload,
        },
        orgDevices: { ...state.orgDevices, [action.payload.uuid]: [] },
        orgUsers: { ...state.orgUsers, [action.payload.uuid]: {} },
      };
    case UPDATE_ORGANIZATION:
      return {
        ...state,
        organizations: {
          ...state.organizations,
          [action.payload.uuid]: action.payload,
        },
      };
    case UPDATE_USER_SELECTED_DEVICE:
      return { ...state, selectedDevice: action.payload?.selectedDevice };
    default:
      return state;
  }
};
